import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import AutomationModal from "./automation-modal";
import { boardKeys } from "./board-query-keys";
import "./project-automations.scss";
import { BoardService } from "./board-service";
import ConfirmDialog from "./confirm-dialog";
import {
  AutomationAction,
  AutomationRule,
  AutomationTrigger,
  BoardConfig,
  BoardStatus,
  PRIORITY_LABELS
} from "./types";
import { activeBoardScope, useBoardConfig } from "./use-board";

interface ProjectAutomationsProps {
  projectKey: string;
}

function statusName(statuses: BoardStatus[], id: string | undefined): string {
  if (!id) return "any status";
  return statuses.find((status: BoardStatus) => status.id === id)?.name ?? id;
}

function describeTrigger(trigger: AutomationTrigger, statuses: BoardStatus[]): string {
  switch (trigger.kind) {
    case "status_changed_to":
      return `status changes to ${statusName(statuses, trigger.statusId)}`;
    case "assignee_empty":
      return `an issue is unassigned as it enters ${statusName(statuses, trigger.statusId)}`;
    case "issue_created":
      return trigger.statusId
        ? `an issue is created in ${statusName(statuses, trigger.statusId)}`
        : "an issue is created";
    case "priority_changed_to":
      return `priority changes to ${PRIORITY_LABELS[trigger.priority]}`;
  }
}

function describeAction(action: AutomationAction, statuses: BoardStatus[]): string {
  switch (action.kind) {
    case "assign_to_me":
      return "assign it to me";
    case "add_label":
      return `add the label “${action.label}”`;
    case "add_comment":
      return "add a comment";
    case "set_priority":
      return `set priority to ${PRIORITY_LABELS[action.priority]}`;
    case "move_to_status":
      return `move it to ${statusName(statuses, action.statusId)}`;
  }
}

// A plain-language "When X, do Y." summary of a rule — far more legible than a bare name.
function describeAutomation(rule: AutomationRule, statuses: BoardStatus[]): string {
  return `When ${describeTrigger(rule.trigger, statuses)}, ${describeAction(rule.action, statuses)}.`;
}

// The project's automations (the hub's Automations tab). Rules are project-scoped — they react to
// this project's issues and act on this project's statuses — so they live alongside the Workflow
// they automate, not in the navigation rail. Visible to everyone (read-only); only managers
// create/toggle/delete, matching the board's visible-but-locked permission model.
const ProjectAutomations: React.FC<ProjectAutomationsProps> = ({ projectKey }) => {
  const { config } = useBoardConfig();
  const queryClient = useQueryClient();
  const canManage: boolean = config.canManage;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<AutomationRule | null>(null);

  const rules: AutomationRule[] = useMemo(
    () => config.automations.filter((rule: AutomationRule) => rule.projectKey === projectKey),
    [config.automations, projectKey]
  );
  const statuses: BoardStatus[] = useMemo(
    () => config.statuses.filter((status: BoardStatus) => status.projectKey === projectKey),
    [config.statuses, projectKey]
  );

  // Patch the shared config cache's automations so create/toggle/delete show instantly, instead of
  // waiting on the full /config refetch BoardService fires afterwards (which then reconciles). Mirrors
  // how the Releases tab patches its versions.
  const patchCachedAutomations = (mutate: (automations: AutomationRule[]) => AutomationRule[]): void => {
    queryClient.setQueryData<BoardConfig>(boardKeys.config(activeBoardScope()), (old: BoardConfig | undefined) =>
      old ? { ...old, automations: mutate(old.automations) } : old
    );
  };

  const handleCreate = (
    name: string,
    trigger: AutomationTrigger,
    action: AutomationAction,
    notifyOnRun: boolean
  ): void => {
    // Show the rule immediately with a temp id; the config.changed refetch reconciles it to the real
    // row (same content → no flicker). Roll the temp row back if the create fails.
    const optimistic: AutomationRule = {
      id: `temp-${crypto.randomUUID()}`,
      projectKey,
      name,
      enabled: true,
      notifyOnRun,
      trigger,
      action
    };
    patchCachedAutomations((automations: AutomationRule[]) => [...automations, optimistic]);
    void BoardService.createAutomation(projectKey, name, trigger, action, notifyOnRun).then(
      (rule: AutomationRule | null) => {
        if (!rule) {
          patchCachedAutomations((automations: AutomationRule[]) =>
            automations.filter((r: AutomationRule) => r.id !== optimistic.id)
          );
        }
      }
    );
  };

  const toggle = (rule: AutomationRule): void => {
    patchCachedAutomations((automations: AutomationRule[]) =>
      automations.map((r: AutomationRule) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r))
    );
    void BoardService.toggleAutomation(rule.id);
  };

  const confirmDelete = (): void => {
    if (!pendingDelete) return;
    const id: string = pendingDelete.id;
    patchCachedAutomations((automations: AutomationRule[]) => automations.filter((r: AutomationRule) => r.id !== id));
    void BoardService.deleteAutomation(id);
    setPendingDelete(null);
  };

  return (
    <div className="kuika-project-automations">
      {!canManage && (
        <div className="kuika-project-automations__readonly-note">
          <KuikaSvg name="lock" width={13} height={13} />
          View only — workspace owners or supervisors can change automations.
        </div>
      )}

      <section className="kuika-project-automations__section">
        <div className="kuika-project-automations__section-head">
          <div>
            <div className="kuika-project-automations__section-title">Automations</div>
            <div className="kuika-project-automations__section-sub">
              Rules that react to this project's issues — when a trigger fires, the action runs automatically.
            </div>
          </div>
          {canManage && (
            <KuikaButton type="secondary" size="sm" onClick={() => setModalOpen(true)}>
              <KuikaSvg name="plus" width={13} height={13} />
              <span style={{ marginLeft: 5 }}>New automation</span>
            </KuikaButton>
          )}
        </div>

        {rules.length === 0 ? (
          <div className="kuika-project-automations__empty">
            <span className="kuika-project-automations__empty-glyph" aria-hidden="true">
              <KuikaSvg name="automation" width={22} height={22} />
            </span>
            <div className="kuika-project-automations__empty-title">No automations yet</div>
            <div className="kuika-project-automations__empty-text">
              {canManage
                ? "Create a rule to move, assign, label, or comment on this project's issues automatically."
                : "No automation rules have been created for this project yet."}
            </div>
          </div>
        ) : (
          <ul className="kuika-project-automations__list">
            {rules.map((rule: AutomationRule) => (
              <li
                key={rule.id}
                className={`kuika-project-automations__row${rule.enabled ? "" : " kuika-project-automations__row--off"}`}
              >
                {canManage ? (
                  <button
                    type="button"
                    className="kuika-project-automations__toggle"
                    onClick={() => toggle(rule)}
                    aria-label={rule.enabled ? `Disable ${rule.name}` : `Enable ${rule.name}`}
                    data-on={rule.enabled ? "true" : "false"}
                  />
                ) : (
                  <span
                    className="kuika-project-automations__toggle"
                    data-on={rule.enabled ? "true" : "false"}
                    aria-label={rule.enabled ? "Enabled" : "Disabled"}
                  />
                )}

                <div className="kuika-project-automations__row-text">
                  <span className="kuika-project-automations__row-name">{rule.name}</span>
                  <span className="kuika-project-automations__row-desc">{describeAutomation(rule, statuses)}</span>
                </div>

                {!rule.notifyOnRun && (
                  <span
                    className="kuika-project-automations__row-silent"
                    title="No notifications are sent when this rule runs (the change is still recorded in history)"
                  >
                    Silent
                  </span>
                )}

                {rule.runCount ? (
                  <span
                    className="kuika-project-automations__row-runs"
                    title={rule.lastRunAt ? `Last ran ${new Date(rule.lastRunAt).toLocaleString("en-US")}` : undefined}
                  >
                    Ran {rule.runCount}×
                  </span>
                ) : (
                  <span className="kuika-project-automations__row-runs kuika-project-automations__row-runs--muted">
                    Never run
                  </span>
                )}

                {canManage && (
                  <KuikaButton
                    type="tertiary"
                    color="neutral"
                    size="sm"
                    isIcon
                    aria-label={`Delete ${rule.name}`}
                    onClick={() => setPendingDelete(rule)}
                  >
                    <KuikaSvg name="trash" width={14} height={14} />
                  </KuikaButton>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <AutomationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        projectKey={projectKey}
        onCreate={handleCreate}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete automation?"
        message={`"${pendingDelete?.name ?? ""}" will stop running. This can't be undone.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ProjectAutomations;
